import { useCallback, useState } from 'react';
import { errorToLogObject } from 'utils/apiErrors';
import { getAccessToken } from 'utils/auth';
import { getBackofficeUrl } from 'utils/urls';

const GENERIC_ERROR =
  'Unable to verify your transaction at this time. Please try again.';

export type RiskEventType =
  | 'login'
  | 'payment'
  | 'account_creation'
  | 'transfer'
  | 'details_change';

export type RiskRunArgs = {
  sessionId: string | null;
  eventType: RiskEventType;
  playerGuid: string;
  amount: number;
  pageId: number;
};

export type RiskResult = {
  ok: boolean;
  kycRequired: boolean;
  message: string;
};

// POST {backoffice}/api/risk
// Strict mirror of Unity LexisNexisModel.PostRisk (LexisNexisModel.cs:70-166):
//   - HTTP non-2xx → fail with server message
//   - 2xx → branch only on `kycRequired` (decision/reasons/tier ignored, matching Unity)
//
// sessionId may be null when TMX is misconfigured client-side; the request is
// still sent so the backoffice (which is the sole authority on bypass) can
// decide. When server-side bypass is on the validator accepts null; otherwise
// it 422s and the cashier surfaces the server's validation message.
export function useRiskAssessment() {
  const [loading, setLoading] = useState(false);

  const runRisk = useCallback(
    async ({
      sessionId,
      eventType,
      playerGuid,
      amount,
      pageId
    }: RiskRunArgs): Promise<RiskResult> => {
      setLoading(true);
      try {
        const jwt = getAccessToken();
        const res = await fetch(`${getBackofficeUrl()}/api/risk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${jwt}`
          },
          body: JSON.stringify({
            sessionId,
            eventType,
            playerGuid,
            amount,
            pageId
          })
        });

        const body = await res.json().catch(() => ({}));

        if (!res.ok) {
          return {
            ok: false,
            kycRequired: false,
            message: body?.message || body?.error || GENERIC_ERROR
          };
        }

        return { ok: true, kycRequired: !!body?.kycRequired, message: '' };
      } catch (e) {
        console.error('[useRiskAssessment] Error:', errorToLogObject(e));
        return { ok: false, kycRequired: false, message: GENERIC_ERROR };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { runRisk, loading };
}
